import { ShieldCheck, ChevronRight, Eye, EyeOff, RotateCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, cn } from '@mustauto/ui/web';
import { useReputation } from '@/hooks/useReputation';

// Estilo por nível do selo. Só classes Tailwind (regra web #11: sem hex).
const LEVEL_STYLE: Record<string, { ring: string; chip: string; bar: string; emoji: string }> = {
  iniciante: {
    ring: 'ring-slate-200 dark:ring-slate-700',
    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    bar: 'bg-slate-400',
    emoji: '🔧',
  },
  confiavel: {
    ring: 'ring-blue-200 dark:ring-blue-800',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    bar: 'bg-blue-500',
    emoji: '🛡️',
  },
  referencia: {
    ring: 'ring-violet-200 dark:ring-violet-800',
    chip: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
    bar: 'bg-violet-500',
    emoji: '⭐',
  },
  excelencia: {
    ring: 'ring-amber-200 dark:ring-amber-700',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    bar: 'bg-amber-500',
    emoji: '🏆',
  },
};

interface ReputationCardProps {
  workshopId: string | null;
}

export function ReputationCard({ workshopId }: ReputationCardProps) {
  const { data, isLoading, isError, refetch } = useReputation(workshopId);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="h-5 w-48 animate-pulse rounded bg-muted-foreground/10" />
        <div className="mt-4 h-16 w-full animate-pulse rounded bg-muted-foreground/10" />
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card className="flex items-center justify-between gap-3 p-6">
        <span className="text-sm text-muted-foreground">
          Não foi possível carregar a reputação da oficina.
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
        >
          <RotateCw size={14} /> Tentar novamente
        </button>
      </Card>
    );
  }

  const style = LEVEL_STYLE[data.level] ?? LEVEL_STYLE.iniciante;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Card className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={cn('flex h-11 w-11 items-center justify-center rounded-full ring-2', style.ring)}>
              <ShieldCheck size={22} className="text-foreground" />
            </div>
            <div>
              <h2 className="text-sm font-medium text-muted-foreground">Selo MustAuto Verificada</h2>
              <div className="mt-0.5 flex items-center gap-2">
                <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', style.chip)}>
                  {style.emoji} {data.levelLabel}
                </span>
                {data.computed && (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 text-xs',
                      data.badgePublic ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
                    )}
                  >
                    {data.badgePublic ? <Eye size={12} /> : <EyeOff size={12} />}
                    {data.badgePublic ? 'Visível para clientes' : 'Selo ainda não exibido'}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold tabular-nums">{data.score}</span>
            <span className="text-sm text-muted-foreground">/100</span>
          </div>
        </div>

        {/* Estado não calculado ainda */}
        {!data.computed ? (
          <p className="mt-4 text-sm text-muted-foreground">
            A reputação da sua oficina será calculada em breve. Abra OS, complete o checklist
            fotográfico e registre a execução para começar a pontuar.
          </p>
        ) : (
          <>
            {/* Barra de progresso para o próximo nível */}
            <div className="mt-4">
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className={cn('h-full rounded-full transition-all', style.bar)} style={{ width: `${data.score}%` }} />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {data.nextLevel
                  ? `Faltam ${data.nextLevel.pointsNeeded} ${data.nextLevel.pointsNeeded === 1 ? 'ponto' : 'pontos'} para o nível ${data.nextLevel.label}`
                  : 'Nível máximo alcançado — parabéns! 🎉'}
                {' · '}
                {data.sampleSize} {data.sampleSize === 1 ? 'OS avaliada' : 'OS avaliadas'} (90 dias)
              </p>
            </div>

            {/* O que falta pra subir */}
            {data.hints.length > 0 && (
              <div className="mt-5">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  O que falta pra subir
                </h3>
                <ul className="mt-2 space-y-2">
                  {data.hints.map((hint) => (
                    <li key={hint.pillar} className="flex items-center gap-3">
                      <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium">{hint.label}</span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{hint.current}%</span>
                        </div>
                        <p className="truncate text-xs text-muted-foreground">{hint.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </Card>
    </motion.div>
  );
}
